import { useEffect, useState } from 'react';
import { useNavigate, useLocation, type Location } from 'react-router-dom';
import { useAuth } from '@leagueplatform/auth-utils';
import { StandaloneAuth } from '@leagueplatform/auth-standalone';
import { ErrorState } from '@leagueplatform/web-common-components';
import { ImpersonationSkeleton } from 'components/loaders/impersonation-skeleton.component';
import { captureAuthenticationError } from 'components/error/auth-error.component';
import { FullPageError } from 'components/error/full-page-error.component';
import { ErrorTypes } from 'hooks/use-error-message.hook';
import { APP_MANAGED_ROUTES } from 'common/constants';
import {
  isImpersonationSession,
  startImpersonationSession,
} from 'utils/auth/impersonation-storage';

const getAuth0ErrorIfExists = (location: Location) => {
  const searchParams = new URLSearchParams(location.search);
  const hashParams = new URLSearchParams(location.hash.replace(/#/g, '?'));

  const type = searchParams.get('error') || hashParams.get('error');
  const description =
    searchParams.get('error_description') ||
    hashParams.get('error_description');

  if (type) {
    return { type, description };
  }

  return null;
};

export const AuthSignIn = () => {
  const AUTH0_CLIENT_CONNECTION = 'cigna';
  const location = useLocation();
  const navigate = useNavigate();

  const [error, setError] = useState('');

  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      navigate({
        pathname: APP_MANAGED_ROUTES.HOME,
      });
    }
  }, [navigate, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated === false) {
      const connection =
        new URLSearchParams(location.search).get('connection') ||
        AUTH0_CLIENT_CONNECTION;

      const authError = getAuth0ErrorIfExists(location);
      if (authError) {
        setError(`${authError.type}: ${authError.description || ''}`);
        captureAuthenticationError(authError);
        return;
      }

      try {
        StandaloneAuth.client.loginWithRedirect({
          connection,
          appState: {
            redirectPath: location.state?.redirectPath,
            invalidateSession: true,
          },
        });
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
      }
    }
  }, [location, navigate, isAuthenticated]);

  if (error) {
    return <FullPageError errorType={ErrorTypes.API_ERROR} />;
  }
  return null;
};

export const AuthStartImpersonationSession = () => {
  const location = useLocation();
  const [error, setError] = useState('');
  const [isInitializing, setIsInitializing] = useState(true);

  const AUTH0_CLIENT_CONNECTION = 'cigna-impersonation-saml';

  useEffect(() => {
    if (!isImpersonationSession()) {
      const searchParams = new URLSearchParams(location.search);
      const originUrl =
        searchParams.get('origin') ||
        searchParams.get('returnUrl') ||
        searchParams.get('redirect_uri');

      startImpersonationSession(originUrl);
      window.location.reload();
      return;
    }

    setIsInitializing(false);
  }, [location.search]);

  useEffect(() => {
    if (isInitializing) return;

    const searchParams = new URLSearchParams(location.search);
    const connection =
      searchParams.get('connection') || AUTH0_CLIENT_CONNECTION;

    try {
      StandaloneAuth.client.loginWithRedirect({ connection });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [isInitializing, location.search]);

  if (error) {
    return <ErrorState bodyString={error} hideButton error={null} />;
  }

  return <ImpersonationSkeleton />;
};
